// ABOUTME: Helpers for Admin CRUD list payload normalization and row filtering.
// ABOUTME: Keeps client-side table logic testable without rendering React.

/**
 * Keys that commonly hold row arrays on API envelopes. When several exist, we
 * pick the longest array so a short `items` page does not hide the full list.
 */
const ROW_LIKE_KEYS = [
  'candidates',
  'clients',
  'users',
  'open_roles',
  'submit_cvroles',
  'transactions',
  'client_invoices',
  'invoices',
  'items',
  'data',
  'results',
  'records',
  'rows',
];

/**
 * @param {Record<string, unknown>} payload
 * @param {string[]} keys
 * @returns {unknown[]|null}
 */
function longestArrayAmongKeys(payload, keys) {
  let best = null;
  let bestLen = -1;
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = payload[k];
    if (Array.isArray(v) && v.length > bestLen) {
      best = v;
      bestLen = v.length;
    }
  }
  return best;
}

/**
 * @param {Record<string, unknown>} payload
 * @returns {unknown[]|null}
 */
function longestArrayAmongAllValues(payload) {
  let best = null;
  let bestLen = -1;
  const vals = Object.values(payload);
  for (let i = 0; i < vals.length; i += 1) {
    const v = vals[i];
    if (Array.isArray(v) && v.length > bestLen) {
      best = v;
      bestLen = v.length;
    }
  }
  return best;
}

/**
 * @param {unknown} payload
 * @returns {object[]|null}
 */
function coerceNumericKeyedObject(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }
  const keys = Object.keys(payload);
  if (!keys.length || !keys.every((k) => /^\d+$/.test(k))) {
    return null;
  }
  return keys.sort((a, b) => Number(a) - Number(b)).map((k) => payload[k]);
}

/**
 * @param {unknown} payload
 * @returns {object[]}
 */
export function normalizeListPayload(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (typeof payload === 'string') {
    try {
      return normalizeListPayload(JSON.parse(payload));
    } catch {
      return [];
    }
  }
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const preferred = longestArrayAmongKeys(payload, ROW_LIKE_KEYS);
  if (preferred && preferred.length > 0) {
    // Envelopes like { items: [...page], data: { candidates: [...full] } } used to
    // return the short top-level array because `data` is an object, not an array.
    if (
      payload.data &&
      typeof payload.data === 'object' &&
      !Array.isArray(payload.data)
    ) {
      const nestedFromData = normalizeListPayload(payload.data);
      if (nestedFromData.length > preferred.length) {
        return nestedFromData;
      }
    }
    return preferred;
  }

  const fallbackArrays = longestArrayAmongAllValues(payload);
  if (fallbackArrays && fallbackArrays.length > 0) {
    return fallbackArrays;
  }

  if (
    payload.data &&
    typeof payload.data === 'object' &&
    !Array.isArray(payload.data)
  ) {
    const nested = normalizeListPayload(payload.data);
    if (nested.length > 0) {
      return nested;
    }
  }

  const topNumeric = coerceNumericKeyedObject(payload);
  if (topNumeric) {
    return topNumeric;
  }

  const dataNumeric = coerceNumericKeyedObject(payload.data);
  if (dataNumeric) {
    return dataNumeric;
  }
  const itemsNumeric = coerceNumericKeyedObject(payload.items);
  if (itemsNumeric) {
    return itemsNumeric;
  }

  const values = Object.values(payload);
  if (values.length === 1 && Array.isArray(values[0])) {
    return values[0];
  }

  return [];
}

/**
 * Short summary of the list endpoint JSON body for the admin footer (diagnostics).
 * @param {unknown} payload
 * @returns {string}
 */
export function describeRawListResponse(payload) {
  if (payload === null || payload === undefined) {
    return 'API body is null or undefined';
  }
  if (Array.isArray(payload)) {
    return `API returned a JSON array with ${payload.length} element(s)`;
  }
  if (typeof payload !== 'object') {
    return `API body type is ${typeof payload}`;
  }
  const keys = Object.keys(payload);
  const arrayParts = keys
    .filter((k) => Array.isArray(payload[k]))
    .map((k) => `${k}=${payload[k].length}`)
    .join(', ');
  if (arrayParts) {
    return `API returned an object; top-level arrays: ${arrayParts} (keys: ${keys.join(', ')})`;
  }
  return `API returned an object with keys: ${keys.join(', ')} (no top-level arrays)`;
}

/**
 * @param {Record<string, unknown>} row
 * @param {Record<string, string>} searchFilters
 * @returns {boolean}
 */
export function rowMatchesFilters(row, searchFilters) {
  return Object.keys(searchFilters).every((field) => {
    const q = searchFilters[field];
    if (q === undefined || q === null || String(q).trim() === '') {
      return true;
    }
    const raw = row[field];
    const value =
      raw === null || raw === undefined ? '' : String(raw).toLowerCase();
    return value.includes(String(q).trim().toLowerCase());
  });
}
